import * as Toast from '@radix-ui/react-toast';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { cn } from '../../lib/cn';

// == Types ==================================================================
export type ToastLevel = 'success' | 'error' | 'warn' | 'info';

interface ToastItem {
  id: number;
  title: string;
  description?: string;
  level: ToastLevel;
}

interface ToastContextValue {
  show: (title: string, description?: string, level?: ToastLevel) => void;
}

// == Context ================================================================
const ToastContext = createContext<ToastContextValue>({ show: () => {} });
export const useToast = () => useContext(ToastContext);

// == Level config ==========================================================─
const LEVEL_CONFIG: Record<
  ToastLevel,
  { icon: React.ReactNode; bar: string; iconBg: string; duration: number }
> = {
  success: {
    bar: 'bg-emerald-500',
    iconBg: 'bg-emerald-50 text-emerald-600',
    duration: 3500,
    icon: (
      <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden>
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  error: {
    bar: 'bg-rose-500',
    iconBg: 'bg-rose-50 text-rose-600',
    duration: 6000,
    icon: (
      <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden>
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  warn: {
    bar: 'bg-amber-400',
    iconBg: 'bg-amber-50 text-amber-600',
    duration: 5000,
    icon: (
      <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden>
        <path d="M8 2L14.5 13.5H1.5L8 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M8 6.5v3M8 11v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  info: {
    bar: 'bg-brand-500',
    iconBg: 'bg-brand-50 text-brand-600',
    duration: 4000,
    icon: (
      <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden>
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 7.5v4M8 5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
};

// == Provider ==============================================================─
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const show = useCallback(
    (title: string, description?: string, level: ToastLevel = 'info') => {
      const id = Date.now() + Math.random();
      setItems((prev) => {
        // Cap stack at 5 — drop oldest
        const next = prev.length >= 5 ? prev.slice(1) : prev;
        return [...next, { id, title, description, level }];
      });
    },
    [],
  );

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ show }}>
      <Toast.Provider swipeDirection="right">
        {children}

        {items.map((t) => {
          const cfg = LEVEL_CONFIG[t.level];
          return (
            <Toast.Root
              key={t.id}
              open
              onOpenChange={(open) => { if (!open) dismiss(t.id); }}
              duration={cfg.duration}
              className={cn(
                'group relative flex w-[340px] max-w-[calc(100vw-32px)] items-start gap-3',
                'overflow-hidden rounded-soft border border-rule bg-surface',
                'shadow-cardLift',
                // slide-in from right
                'data-[state=open]:animate-slideInRight',
                'data-[state=closed]:animate-slideOutRight',
                'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
                'data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-transform',
                'data-[swipe=end]:animate-slideOutRight',
              )}
            >
              {/* level bar */}
              <div className={cn('absolute left-0 top-0 h-full w-[3px]', cfg.bar)} />

              {/* icon */}
              <div className={cn('ml-4 mt-3.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md', cfg.iconBg)}>
                {cfg.icon}
              </div>

              {/* content */}
              <div className="flex-1 py-3 pr-2">
                <Toast.Title className="text-[13.5px] font-semibold leading-snug text-ink">
                  {t.title}
                </Toast.Title>
                {t.description && (
                  <Toast.Description className="mt-0.5 text-[12px] leading-relaxed text-ash">
                    {t.description}
                  </Toast.Description>
                )}
              </div>

              {/* dismiss */}
              <Toast.Action altText="Dismiss" asChild>
                <button
                  onClick={() => dismiss(t.id)}
                  className="mr-2 mt-2.5 flex h-6 w-6 shrink-0 items-center justify-center rounded text-ash opacity-0 transition-opacity hover:bg-paper hover:text-ink group-hover:opacity-100"
                  aria-label="Dismiss"
                >
                  <svg viewBox="0 0 12 12" fill="none" className="h-3 w-3">
                    <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </Toast.Action>
            </Toast.Root>
          );
        })}

        <Toast.Viewport className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 outline-none" />
      </Toast.Provider>
    </ToastContext.Provider>
  );
}
